import type { APIError } from '@vizit/api';
import type { ImageView } from '@vizit/api';
import { createContext, useContext } from 'react';
import type { SparkState } from '../../components/asset-details-sparks-tab-panel/asset-details-sparks-tab-panel.hooks';
import type { AssetDetailsTabOptions } from '../../components/asset-details-tabs';
import type { SparkImage } from '../../components/spark-images-grid/spark-images-grid';
import type { CustomPair } from '../../hooks/use-custom-pair-score';
import { isGeneratedImageSelected } from '../../utils';
import type { AssetDetailsOrigin } from '../../utils';

enum AnalysisArtifacts {
  OriginalImage = 'original_image',
  AttentionMap = 'attention_map',
  GazeSequence = 'gaze_sequence',
  VisualAppealDrivers = 'visual_appeal_drivers',
  VisualAppealDetractors = 'visual_appeal_detractors',
  FocalPoints = 'focal_points',
}

interface AssetDetailsContextType {
  /**
   * The asset ID.
   */
  assetId: string;
  /**
   * The app surface the Asset Details page was opened from. Drives whether a
   * class change warns via the score-impact dialog (pdp) or applies immediately
   * (library / collection). Undefined on deep-link/refresh — treated as "warn".
   */
  origin?: AssetDetailsOrigin;
  /**
   * The collection the asset was opened from, when any. Names the (benchmark, product category)
   * pair the header's score and "Scored against" box describe. Undefined outside a collection.
   */
  collectionId?: string;
  /**
   * The custom benchmark the collection showed with no category under it, as a picker key (`custom:<id>`), when the
   * asset was opened from there. Until the collection's own pair is under it, the asset is not scored against any.
   */
  awaitingCategoryBenchmarkKey?: string;
  /**
   * The currently selected tab.
   */
  currentTab: AssetDetailsTabOptions;
  /**
   * The portfolio ID.
   */
  workspaceId: string;
  /**
   * The organization ID.
   */
  organizationId: string;
  /**
   * The currently selected analysis artifact for with the current image.
   */
  selectedArtifact: AnalysisArtifacts;
  /**
   * The function to call when the selected analysis artifact changes.
   */
  onArtifactChange: (option: AnalysisArtifacts) => void;
  /**
   * The function to call when the selected tab changes.
   */
  onTabChange: (tab: AssetDetailsTabOptions) => void;
  /**
   * The function to call when the asset details fail to load
   *
   * @param error - The error that occurred
   */
  onAuthenticationError?: (error: unknown | APIError) => void;
  /**
   * The current state of the Spark tab
   */
  sparkState: SparkState;
  /**
   * The content of the Spark tab
   */
  sparkContent: string | null;
  /**
   * The function to call when the Spark tab is opened to generate an idea
   */
  generateIdea: () => Promise<void>;
  /**
   * The currently selected generated image ID in the Spark Images tab
   * Defaults to the original asset ID when no generated image is selected
   */
  selectedGeneratedImageId: string;
  /**
   * The function to call when a generated image is selected in the Spark Images tab
   */
  onSelectedGeneratedImageChange: (imageId: string) => void;
  /**
   * The function to call when the jump-to hover action is pressed on a
   * generated image in the Spark Images tab. Navigates to that image's own
   * details page. When not provided, the jump-to action is not shown.
   */
  onJumpToImage?: (imageId: string) => void;
  /**
   * The function to call when the back overlay action is pressed on the image
   * preview. Navigates to the parent (source) image's details page. When not
   * provided, the back action is not shown.
   */
  onBackToImage?: (imageId: string) => void;
  /**
   * Whether the Spark Images tab hides generated images that do not score
   * higher than the original image. Defaults to true (on).
   */
  hideImagesNotHigherThanOriginal: boolean;
  /**
   * The function to call when the "hide images not higher than the original"
   * toggle changes in the Spark Images tab.
   */
  onHideImagesNotHigherThanOriginalChange: (hide: boolean) => void;
  /**
   * The original image data from the API
   */
  originalImageData: ImageView | null;
  /**
   * Whether the original image data is currently loading
   */
  isOriginalImageLoading: boolean;
  /**
   * The pair the original image's collection selected, a custom benchmark or a retailer's, or `null` without one. The
   * Spark Images grid reads the generated images' scores under it.
   */
  customPair: CustomPair | null;
  /**
   * The original image's score under the custom benchmark its collection selected, or `null` without one. Spark
   * learns from it in place of the image's own score.
   */
  customPairScore: number | null;
  /**
   * The unrounded score behind `customPairScore`, which the Spark Images hide filter compares.
   */
  customPairRawScore: number | null;
  /**
   * The score the original image is shown and compared by: its score under the collection's selected pair
   * (a retailer's included) when scored, `null` while the collection or its pair has not answered, else its own display
   * score. Matches the details header.
   */
  originalScore: number | null;
  /** The unrounded score behind `originalScore`, which the Spark Images hide filter compares */
  originalRawScore: number | null;
  /**
   * Function to trigger generation of more images manually.
   *
   * @param prompt - What the next batch should change about the source image.
   * Omitting it (or passing an empty value) is a plain "generate more".
   * @returns Resolves once the batch request has been accepted. Rejects with
   * the request error after it has been surfaced to the user, so a caller can
   * keep its own state (such as the typed prompt) on failure.
   */
  generateMoreImages: (prompt?: string) => Promise<void>;
  /**
   * Array of spark images with skeletons when generation is in progress
   */
  sparkImages: SparkImage[];
  /**
   * Whether spark images are currently loading
   */
  isSparkImagesLoading: boolean;
  /**
   * Whether there was an error loading spark images
   */
  hasSparkImagesError: boolean;
  /**
   * Whether there are more spark images pages to fetch
   */
  hasSparkImagesNextPage: boolean;
  /**
   * Whether the next page of spark images is being fetched
   */
  isSparkImagesFetchingNextPage: boolean;
  /**
   * Function to fetch the next page of spark images
   */
  fetchSparkImagesNextPage: () => Promise<unknown>;
  /**
   * Total count of spark images
   */
  sparkImagesTotalCount: number;
  /**
   * Whether image generation is in progress
   */
  isGenerating?: boolean;
  /**
   * Whether the batch has a generation error
   */
  hasBatchGenerationError?: boolean;
  /**
   * The error message from the batch status
   */
  batchGenerationErrorMessage?: string;
}

const AssetDetailsContext = createContext<AssetDetailsContextType | null>(null);

const useAssetDetails = () => {
  const context = useContext(AssetDetailsContext);

  if (!context) {
    throw new Error('useAssetDetails must be used within an AssetDetailsProvider');
  }

  return context;
};

/**
 * Hook to detect if a generated image is currently selected
 * @returns true if a generated image is selected, false if the original image is selected
 */
const useIsGeneratedImageSelected = () => {
  const { selectedGeneratedImageId, assetId } = useAssetDetails();
  return isGeneratedImageSelected(selectedGeneratedImageId, assetId);
};

export { AnalysisArtifacts, AssetDetailsContext, useAssetDetails, useIsGeneratedImageSelected };
export type { AssetDetailsContextType };
